import { useRef, useState } from 'react'
import { Check, Loader2, Monitor, Moon, RotateCcw, Sun, Trash2, Undo2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { resizeImageToDataUrl } from '@/lib/imageUpload'
import { ColorField, LogoField, type ThemeMode } from './IdentityCard'
import {
  BACKGROUND_FIT_OPTIONS,
  BACKGROUND_STRENGTH_OPTIONS,
  BUTTON_SHAPE_OPTIONS,
  CARD_STYLE_OPTIONS,
  FONT_CATALOGUE,
  HEADING_CASE_OPTIONS,
  HERO_VARIANT_OPTIONS,
  LOOKS,
  MENU_LAYOUT_OPTIONS,
  NAV_STYLE_OPTIONS,
  RADIUS_OPTIONS,
  TEXTURE_OPTIONS,
  type LookMeta,
  type StorefrontLook,
} from './storefrontConfig'

// ── Shared pieces ───────────────────────────────────────────────────────────

const HEX = /^#[0-9a-fA-F]{6}$/

type Opt = { id: string; label: string; hint?: string }

/**
 * One design axis as a row of tiles. The first tile is always "the theme's
 * own" (value ''), labelled with what that is, so a tenant can see the
 * default and get back to it in one click.
 */
export function OptionGroup({
  label,
  hint,
  value,
  themeValue,
  options,
  onChange,
  render,
  disabled,
  rows,
}: {
  label: string
  hint?: string
  value: string
  themeValue: string
  options: Opt[]
  onChange: (v: string) => void
  /** Optional mini picture of an option, drawn above its label. */
  render?: (id: string) => React.ReactNode
  disabled?: boolean
  /** One option per row with its hint spelled out, for long lists of choices. */
  rows?: boolean
}) {
  const themeLabel = options.find((o) => o.id === themeValue)?.label ?? themeValue
  const current = options.find((o) => o.id === (value || themeValue))
  const tiles: Array<Opt & { value: string }> = [
    { id: '', value: '', label: `Theme (${themeLabel})` },
    ...options.map((o) => ({ ...o, value: o.id })),
  ]
  if (rows) {
    return (
      <fieldset className={cn('space-y-2', disabled && 'pointer-events-none opacity-50')} disabled={disabled}>
        <legend className="text-sm font-medium">{label}</legend>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        <div className="overflow-hidden rounded-lg border">
          {tiles.map((t) => {
            const selected = value === t.value
            return (
              <button
                key={t.id || 'theme'}
                type="button"
                onClick={() => onChange(t.value)}
                aria-pressed={selected}
                className={cn(
                  'flex w-full items-center gap-3 border-b px-3 py-2 text-left transition-colors last:border-b-0',
                  selected ? 'bg-primary/5' : 'hover:bg-muted/50',
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className={cn('block text-sm font-medium', !t.value && 'text-muted-foreground')}>{t.label}</span>
                  {t.hint && <span className="block text-xs leading-snug text-muted-foreground">{t.hint}</span>}
                </span>
                <span
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                    selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                  )}
                  aria-hidden
                >
                  {selected && <Check className="h-3 w-3" />}
                </span>
              </button>
            )
          })}
        </div>
      </fieldset>
    )
  }
  return (
    <fieldset className={cn('space-y-2', disabled && 'pointer-events-none opacity-50')} disabled={disabled}>
      <legend className="text-sm font-medium">{label}</legend>
      <div className={cn('grid gap-2', tiles.length === 4 ? 'grid-cols-4' : 'grid-cols-3')}>
        {tiles.map((t) => {
          const selected = value === t.value
          const pictureId = t.value || themeValue
          return (
            <button
              key={t.id || 'theme'}
              type="button"
              onClick={() => onChange(t.value)}
              aria-pressed={selected}
              title={t.hint}
              className={cn(
                'flex min-w-0 flex-col items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-center text-xs leading-tight transition',
                selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:border-foreground/30',
              )}
            >
              {render && <span className="flex h-8 items-center justify-center">{render(pictureId)}</span>}
              <span className={cn('font-medium', !t.value && 'text-muted-foreground')}>{t.label}</span>
            </button>
          )
        })}
      </div>
      <p className="text-[11px] text-muted-foreground">
        {current?.hint ? `${current.hint}. ` : ''}
        {hint}
      </p>
    </fieldset>
  )
}

export function PanelIntro({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-muted-foreground">{children}</p>
}

// ── Theme ───────────────────────────────────────────────────────────────────

/** The themes as small pages, drawn in their own colours, type and shapes. */
export function ThemeGallery({
  look,
  brand,
  name,
  onLook,
  tuned,
  onResetTuning,
  previous,
  onUndo,
}: {
  look: StorefrontLook
  brand: string
  name: string
  onLook: (v: StorefrontLook) => void
  /** How many design choices differ from the theme's own. */
  tuned: number
  onResetTuning: () => void
  /** The theme they just switched away from, while going back is possible. */
  previous: string | null
  onUndo: () => void
}) {
  const current = look || 'counter'
  const meta = LOOKS.find((l) => l.id === current) ?? LOOKS[0]
  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-muted/40 p-3">
        <div className="text-sm font-semibold">{meta.label}</div>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{meta.blurb}</p>
        {(previous || tuned > 0) && (
          <div className="mt-2 flex flex-wrap gap-2">
            {previous && (
              <Button type="button" variant="outline" size="sm" onClick={onUndo} className="gap-1.5">
                <Undo2 className="h-4 w-4" /> Go back to {previous}
              </Button>
            )}
            {tuned > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onResetTuning}
                className="gap-1.5"
                title="Use the theme's own fonts, shapes, layout and page colour"
              >
                <RotateCcw className="h-4 w-4" /> Reset {tuned} {tuned === 1 ? 'change' : 'changes'} to the theme
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {LOOKS.map((l) => (
          <ThemeCard key={l.id} meta={l} brand={brand} name={name} selected={current === l.id} onSelect={() => onLook(l.id)} />
        ))}
      </div>
      <PanelIntro>
        Choosing a theme starts its fonts, shapes, layout and page colour fresh. Your brand colours, logo, photos and
        words stay.
      </PanelIntro>
    </div>
  )
}

function ThemeCard({
  meta,
  brand,
  name,
  selected,
  onSelect,
}: {
  meta: LookMeta
  brand: string
  name: string
  selected: boolean
  onSelect: () => void
}) {
  const btnRadius = meta.buttonShape === 'pill' ? 999 : meta.buttonShape === 'rounded' ? 6 : 1
  const cardRadius = meta.radius === 'sharp' ? 3 : meta.radius === 'pill' ? 12 : 7
  const fg = readableOn(brand)
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      title={meta.blurb}
      className={cn(
        'group min-w-0 overflow-hidden rounded-lg border text-left transition',
        selected ? 'border-primary ring-2 ring-primary' : 'border-border hover:border-foreground/30',
      )}
    >
      <div className="relative h-[5.5rem] p-2.5" style={{ background: meta.canvas, color: meta.ink }}>
        <div
          className="truncate pr-5 text-base leading-none"
          style={{
            fontFamily: `'${meta.fontHeading}', serif`,
            textTransform: meta.headingCase === 'upper' ? 'uppercase' : 'none',
            fontStyle: meta.headingItalic ? 'italic' : 'normal',
          }}
        >
          {name}
        </div>
        <div className="absolute inset-x-2.5 bottom-2.5 flex items-center gap-1.5">
          <div
            className="h-7 min-w-0 flex-1 p-1.5"
            style={{
              background: meta.surface,
              borderRadius: cardRadius,
              ...(meta.cardStyle === 'sticker'
                ? { border: `2px solid ${meta.ink}`, boxShadow: `2px 2px 0 ${meta.ink}` }
                : null),
            }}
          >
            <div className="h-1 w-2/3 rounded-full" style={{ background: meta.ink, opacity: 0.8 }} />
            <div className="mt-1 h-1 w-1/3 rounded-full" style={{ background: meta.ink, opacity: 0.35 }} />
          </div>
          <span
            className="px-2 py-1 text-[10px] font-semibold leading-none"
            style={{ background: brand, color: fg, borderRadius: btnRadius }}
          >
            Add
          </span>
        </div>
        {selected && (
          <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Check className="h-3 w-3" />
          </span>
        )}
      </div>
      <div className="border-t px-2.5 py-2">
        <div className="text-sm font-semibold">{meta.label}</div>
        <div className="text-xs leading-snug text-muted-foreground">{meta.bestFor}</div>
      </div>
    </button>
  )
}

// ── Colours ─────────────────────────────────────────────────────────────────

const LIGHT_SUGGESTIONS = ['#FFFFFF', '#F4F4F0', '#FFF4F1', '#EEF4EC', '#F2EDE3', '#EDF2F7']
const DARK_SUGGESTIONS = ['#121211', '#1C1512', '#101A2A', '#10201A', '#1E1426', '#221A0E']

export function ColoursPanel(p: {
  meta: LookMeta
  brand: string
  accent: string
  canvasLight: string
  canvasDark: string
  mode: ThemeMode
  brandPlaceholder: string
  accentPlaceholder: string
  onBrand: (v: string) => void
  onAccent: (v: string) => void
  onCanvasLight: (v: string) => void
  onCanvasDark: (v: string) => void
  onMode: (v: ThemeMode) => void
  logo: string
  logoPlaceholder: string
  onLogo: (v: string) => void
}) {
  const effBrand = HEX.test(p.brand) ? p.brand : p.brandPlaceholder
  const effLight = HEX.test(p.canvasLight) ? p.canvasLight : p.meta.canvasLight
  const effDark = HEX.test(p.canvasDark) ? p.canvasDark : p.meta.canvasDark
  const brandOnLight = contrastRatio(effBrand, effLight)
  const brandOnDark = contrastRatio(effBrand, effDark)

  return (
    <div className="space-y-6">
      <LogoField logo={p.logo} placeholder={p.logoPlaceholder} onChange={p.onLogo} />
      <div className="space-y-4 border-t pt-5">
        <ColorField
          label="Brand colour"
          hint="Buttons, the hero block, active tabs."
          value={p.brand}
          placeholder={p.brandPlaceholder}
          onChange={p.onBrand}
          invalid={p.brand.trim() !== '' && !HEX.test(p.brand)}
        />
        <ColorField
          label="Accent colour"
          hint="Deals and small highlights."
          value={p.accent}
          placeholder={p.accentPlaceholder}
          onChange={p.onAccent}
          invalid={p.accent.trim() !== '' && !HEX.test(p.accent)}
        />
      </div>

      <div className="space-y-4 border-t pt-5">
        <PanelIntro>
          Text colours are worked out for you from the page colour, so the menu stays readable whatever you pick.
        </PanelIntro>
        <CanvasField
          label="Page colour, light mode"
          value={p.canvasLight}
          themeValue={p.meta.canvasLight}
          suggestions={LIGHT_SUGGESTIONS}
          onChange={p.onCanvasLight}
          warning={brandOnLight < 3 ? 'Your brand colour is hard to see on this page colour.' : null}
        />
        <CanvasField
          label="Page colour, dark mode"
          value={p.canvasDark}
          themeValue={p.meta.canvasDark}
          suggestions={DARK_SUGGESTIONS}
          onChange={p.onCanvasDark}
          warning={brandOnDark < 2.2 ? 'Your brand colour is hard to see on this page colour.' : null}
        />
      </div>

      <div className="space-y-2 border-t pt-5">
        <Label>Opens in</Label>
        <div className="grid grid-cols-2 gap-2">
          {(
            [
              { id: '', label: p.meta.darkFirst ? 'Theme (dark)' : 'Theme (light)', icon: null },
              { id: 'system', label: 'Phone setting', icon: Monitor },
              { id: 'light', label: 'Light', icon: Sun },
              { id: 'dark', label: 'Dark', icon: Moon },
            ] as const
          ).map((opt) => {
            const Icon = opt.icon
            return (
              <Button
                key={opt.id || 'theme'}
                type="button"
                variant={p.mode === opt.id ? 'default' : 'outline'}
                size="sm"
                className="gap-1.5"
                onClick={() => p.onMode(opt.id)}
              >
                {Icon && <Icon className="h-4 w-4" />}
                {opt.label}
              </Button>
            )
          })}
        </div>
        <p className="text-[11px] text-muted-foreground">Customers can still switch light or dark themselves.</p>
      </div>
    </div>
  )
}

function CanvasField({
  label,
  value,
  themeValue,
  suggestions,
  onChange,
  warning,
}: {
  label: string
  value: string
  themeValue: string
  suggestions: string[]
  onChange: (v: string) => void
  warning: string | null
}) {
  const custom = HEX.test(value)
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap items-center gap-1.5">
        <Swatch colour={themeValue} selected={!custom} onClick={() => onChange('')} label="Theme's own" />
        {suggestions.map((c) => (
          <Swatch key={c} colour={c} selected={custom && value.toLowerCase() === c.toLowerCase()} onClick={() => onChange(c)} />
        ))}
        <label
          className={cn(
            'relative flex h-8 cursor-pointer items-center gap-1.5 rounded-md border px-2 text-xs',
            custom && !suggestions.some((c) => c.toLowerCase() === value.toLowerCase()) ? 'border-primary ring-1 ring-primary' : '',
          )}
        >
          <span className="h-4 w-4 rounded-sm border" style={{ background: custom ? value : themeValue }} />
          Custom
          <input
            type="color"
            className="absolute inset-0 cursor-pointer opacity-0"
            value={custom ? value : themeValue}
            onChange={(e) => onChange(e.target.value)}
          />
        </label>
      </div>
      <p className={cn('text-[11px]', warning ? 'text-amber-600' : 'text-muted-foreground')}>
        {warning ?? (custom ? `${value.toUpperCase()}. Cards, lines and text are matched to it.` : `The theme's ${themeValue}.`)}
      </p>
    </div>
  )
}

function Swatch({ colour, selected, onClick, label }: { colour: string; selected: boolean; onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label ?? colour}
      aria-label={label ?? colour}
      aria-pressed={selected}
      className={cn(
        'flex h-8 items-center gap-1.5 rounded-md border px-1.5 text-xs',
        selected ? 'border-primary ring-1 ring-primary' : 'border-border',
      )}
    >
      <span className="h-5 w-5 rounded-sm border border-black/10" style={{ background: colour }} />
      {label && <span className="pr-1">{label}</span>}
    </button>
  )
}

// ── Fonts ───────────────────────────────────────────────────────────────────

export function FontsPanel(p: {
  meta: LookMeta
  heading: string
  body: string
  headingCase: string
  sample: string
  onHeading: (v: string) => void
  onBody: (v: string) => void
  onHeadingCase: (v: string) => void
}) {
  const [part, setPart] = useState<'heading' | 'body'>('heading')
  return (
    <div className="space-y-5">
      <div className="flex gap-1 rounded-lg bg-muted/60 p-1" role="tablist" aria-label="Which text">
        {(
          [
            { id: 'heading', label: 'Headings', face: p.heading || p.meta.fontHeading },
            { id: 'body', label: 'Body text', face: p.body || p.meta.fontBody },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={part === t.id}
            onClick={() => setPart(t.id)}
            className={cn(
              'min-w-0 flex-1 rounded-md px-3 py-1.5 text-left transition-colors',
              part === t.id ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <span className="block text-sm font-medium">{t.label}</span>
            <span className="block truncate text-xs text-muted-foreground">{t.face}</span>
          </button>
        ))}
      </div>
      {part === 'heading' ? (
        <>
          <OptionGroup
            label="Capitals"
            value={p.headingCase}
            themeValue={p.meta.headingCase}
            options={HEADING_CASE_OPTIONS}
            onChange={p.onHeadingCase}
          />
          <FontPicker label="Typeface" value={p.heading} themeFont={p.meta.fontHeading} sample={p.sample} onChange={p.onHeading} />
        </>
      ) : (
        <>
          <PanelIntro>Body text is read at small sizes, so the plainer faces work best here.</PanelIntro>
          <FontPicker label="Typeface" value={p.body} themeFont={p.meta.fontBody} sample="Grilled chicken, rice" onChange={p.onBody} />
        </>
      )}
    </div>
  )
}

function FontPicker({
  label,
  value,
  themeFont,
  sample,
  onChange,
}: {
  label: string
  value: string
  themeFont: string
  sample: string
  onChange: (v: string) => void
}) {
  const groups = ['Sans', 'Serif', 'Display'] as const
  const tile = (name: string, v: string, caption: string) => {
    const selected = value === v
    return (
      <button
        key={v || 'theme'}
        type="button"
        onClick={() => onChange(v)}
        aria-pressed={selected}
        className={cn(
          'flex min-w-0 flex-col items-start rounded-lg border px-3 py-2 text-left transition',
          selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:border-foreground/30',
        )}
      >
        <span className="w-full truncate text-lg leading-tight" style={{ fontFamily: `'${name}', sans-serif` }}>
          {sample}
        </span>
        <span className="mt-0.5 text-[11px] text-muted-foreground">{caption}</span>
      </button>
    )
  }
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="grid grid-cols-1 gap-2">{tile(themeFont, '', `Theme (${themeFont})`)}</div>
      {groups.map((g) => (
        <div key={g} className="space-y-1.5">
          <div className="pt-1 text-[11px] font-medium text-muted-foreground">{g}</div>
          <div className="grid grid-cols-2 gap-2">
            {FONT_CATALOGUE.filter((f) => f.group === g).map((f) => tile(f.name, f.name, f.name))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Shape ───────────────────────────────────────────────────────────────────

export function ShapePanel(p: {
  meta: LookMeta
  radius: string
  buttonShape: string
  cardStyle: string
  brand: string
  onRadius: (v: string) => void
  onButtonShape: (v: string) => void
  onCardStyle: (v: string) => void
}) {
  const fg = readableOn(p.brand)
  return (
    <div className="space-y-6">
      <OptionGroup
        label="Card and photo corners"
        value={p.radius}
        themeValue={p.meta.radius}
        options={RADIUS_OPTIONS}
        onChange={p.onRadius}
        render={(id) => (
          <span
            className="block h-7 w-10 border-2 border-foreground/60"
            style={{ borderRadius: id === 'sharp' ? 3 : id === 'pill' ? 12 : 7 }}
          />
        )}
      />
      <OptionGroup
        label="Buttons"
        value={p.buttonShape}
        themeValue={p.meta.buttonShape}
        options={BUTTON_SHAPE_OPTIONS}
        onChange={p.onButtonShape}
        render={(id) => (
          <span
            className="px-3 py-1 text-[10px] font-semibold"
            style={{ background: p.brand, color: fg, borderRadius: id === 'pill' ? 999 : id === 'rounded' ? 6 : 1 }}
          >
            Add
          </span>
        )}
      />
      <OptionGroup
        label="Menu items"
        value={p.cardStyle}
        themeValue={p.meta.cardStyle}
        options={CARD_STYLE_OPTIONS}
        onChange={p.onCardStyle}
        render={(id) => (
          <span
            className={cn(
              'block h-7 w-10 rounded',
              id === 'raised' && 'border bg-background shadow',
              id === 'outline' && 'border-2 border-foreground/40',
              id === 'flat' && 'bg-foreground/10',
              id === 'bare' && 'border-b-2 border-foreground/30',
              id === 'sticker' && 'border-2 border-foreground bg-background shadow-[2px_2px_0_hsl(var(--foreground))]',
            )}
          />
        )}
      />
    </div>
  )
}

// ── Background ──────────────────────────────────────────────────────────────

export function BackgroundPanel(p: {
  meta: LookMeta
  texture: string
  image: string
  fit: string
  strength: string
  onTexture: (v: string) => void
  onImage: (v: string) => void
  onFit: (v: string) => void
  onStrength: (v: string) => void
}) {
  const { toast } = useToast()
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const tile = p.fit === 'tile'

  const upload = async (file: File | undefined) => {
    if (fileRef.current) fileRef.current.value = ''
    if (!file) return
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
      toast({ title: 'Not a supported image', description: 'Choose a PNG, JPEG or WebP file.', variant: 'destructive' })
      return
    }
    if (file.size > 8 * 1024 * 1024) {
      toast({ title: 'Image too large', description: 'Choose a file under 8 MB.', variant: 'destructive' })
      return
    }
    setBusy(true)
    try {
      // A pattern tile only needs to be small; a full-screen photo needs width.
      p.onImage(await resizeImageToDataUrl(file, tile ? 640 : 1920, 0.8))
    } catch (err) {
      toast({ title: 'Could not process image', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Background image</Label>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        <div className="flex items-center gap-3">
          <div
            className="h-16 w-24 shrink-0 rounded-lg border bg-muted"
            style={
              p.image
                ? { backgroundImage: `url("${p.image}")`, backgroundSize: tile ? '32px' : 'cover', backgroundPosition: 'center' }
                : undefined
            }
          />
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" className="gap-1.5" disabled={busy} onClick={() => fileRef.current?.click()}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {p.image ? 'Replace' : 'Upload image'}
            </Button>
            {p.image && (
              <Button type="button" variant="ghost" size="sm" className="gap-1.5" onClick={() => p.onImage('')}>
                <Trash2 className="h-4 w-4" />
                Remove
              </Button>
            )}
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          A wide photo for "Fill the screen", or a small square design for "Repeat as a pattern". Under 8 MB.
        </p>
      </div>

      <OptionGroup
        label="How it sits"
        value={p.fit}
        themeValue="cover"
        options={BACKGROUND_FIT_OPTIONS}
        onChange={p.onFit}
        disabled={!p.image}
      />
      <OptionGroup
        label="How much shows through"
        value={p.strength}
        themeValue="soft"
        options={BACKGROUND_STRENGTH_OPTIONS}
        onChange={p.onStrength}
        disabled={!p.image}
      />
      <OptionGroup
        label="Texture"
        hint="A fine finish over the page colour."
        value={p.texture}
        themeValue={p.meta.texture}
        options={TEXTURE_OPTIONS}
        onChange={p.onTexture}
      />
    </div>
  )
}

// ── Layout ──────────────────────────────────────────────────────────────────

export function LayoutPanel(p: {
  meta: LookMeta
  heroVariant: string
  menuLayout: string
  navStyle: string
  onHeroVariant: (v: string) => void
  onMenuLayout: (v: string) => void
  onNavStyle: (v: string) => void
}) {
  return (
    <div className="space-y-6">
      <OptionGroup
        label="Top of the page"
        hint="Table QR customers skip this and land on the menu."
        value={p.heroVariant}
        themeValue={p.meta.heroVariant}
        options={HERO_VARIANT_OPTIONS}
        onChange={p.onHeroVariant}
        rows
      />
      <OptionGroup
        label="Menu"
        hint="In the photo layouts, a category with no photos shows as a list."
        value={p.menuLayout}
        themeValue={p.meta.menuLayout}
        options={MENU_LAYOUT_OPTIONS}
        onChange={p.onMenuLayout}
        rows
      />
      <OptionGroup
        label="Category navigation"
        value={p.navStyle}
        themeValue={p.meta.navStyle}
        options={NAV_STYLE_OPTIONS}
        onChange={p.onNavStyle}
      />
    </div>
  )
}

// ── Colour maths (mirrors the storefront's lib/canvas.ts) ──────────────────

function rgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}
function lum(hex: string): number {
  const c = rgb(hex)
  if (!c) return 1
  const ch = (v: number) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * ch(c[0]) + 0.7152 * ch(c[1]) + 0.0722 * ch(c[2])
}
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [lum(a), lum(b)].sort((x, y) => y - x)
  return (hi + 0.05) / (lo + 0.05)
}
function readableOn(hex: string): string {
  return contrastRatio(hex, '#161513') >= contrastRatio(hex, '#ffffff') ? '#161513' : '#ffffff'
}
